import React, { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Upload, TrendingUp, TrendingDown, Target, AlertTriangle, CheckCircle, XCircle,
  Loader2, Save, Image as ImageIcon, Zap, Clock, Calendar, RefreshCw,
  DollarSign, Layers, X, BookOpen, BarChart3, Shield, Scale, Flame,
  ChevronDown, Star, Search, FileText, SlidersHorizontal, RotateCcw, Percent,
  ZoomIn, Maximize2, Sparkles, Trophy, Camera, ChartBar
} from 'lucide-react';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import axios from 'axios';
import OnboardingTutorial from '../components/OnboardingTutorial';
import LegalDisclaimer, { useDisclaimerCheck } from '../components/LegalDisclaimer';
import { ANALYSIS_SLIDES } from '../config/tutorialSlides';
import { getSymbolName, getSymbolInfo } from '../utils/symbolNames';
import TradingViewLink from '../components/TradingViewLink';
import { useEstimatedFees } from '../hooks/useEstimatedFees';
import HowItWorks from '../components/HowItWorks';
import EstimatedFeesField from '../components/EstimatedFeesField';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Profile configuration with leverage
const PROFILES = {
  prudent: {
    key: 'prudent',
    label: { fr: 'Prudent', en: 'Prudent' },
    description: { fr: 'Levier 10x • TP proche • R:R ~2:1', en: 'Leverage 10x • Close TP • R:R ~2:1' },
    leverageInfo: { fr: 'Gains rapides, moins exposé au retournement', en: 'Quick gains, less exposure to reversal' },
    defaultLeverage: 10,
    icon: Shield,
    color: 'text-green-400',
    bgColor: 'bg-green-500/20',
    borderColor: 'border-green-500/30'
  },
  balanced: {
    key: 'balanced',
    label: { fr: 'Équilibré', en: 'Balanced' },
    description: { fr: 'Levier 20x • TP moyen • R:R ~3:1', en: 'Leverage 20x • Medium TP • R:R ~3:1' },
    leverageInfo: { fr: 'Compromis gains/exposition', en: 'Balance gains/exposure' },
    defaultLeverage: 20,
    icon: Scale,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/20',
    borderColor: 'border-yellow-500/30'
  },
  dynamic: {
    key: 'dynamic',
    label: { fr: 'Dynamique', en: 'Dynamic' },
    description: { fr: 'Levier 30x • TP éloigné • R:R ~5:1', en: 'Leverage 30x • Far TP • R:R ~5:1' },
    leverageInfo: { fr: 'Gros gains potentiels, plus exposé au retournement ⚠️', en: 'Big potential gains, more exposure to reversal ⚠️' },
    defaultLeverage: 30,
    icon: Flame,
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
    borderColor: 'border-red-500/30'
  }
};

// Chart Upload Component — REMOVED (2026-02): manual chart upload was retired
// to guarantee AI accuracy. Users may add custom indicators / drawings / templates
// that the AI wasn't trained on, which produces unreliable reads. The dashboard
// now only uses the auto-generated standardized chart path.
//
// If you need to reintroduce a "power user" upload mode, gate it behind
// tier === 'expert' AND add a clear disclaimer about reduced precision.

// Trade Card Component with Profile Selector
const TradeCard = ({ type, analysis, language, symbol, onSaveTrade, capital: initialCapital = 10000, risk: initialRisk = 1 }) => {
  const isSwing = type === 'swing';
  const prefix = isSwing ? 'swing_' : 'intraday_';
  const dir = analysis[`${prefix}direction`];
  const isLong = dir === 'LONG';
  const [saving, setSaving] = useState(false);
  
  // Mode: "optimal" (IA entry) or "market" (current price)
  const [entryMode, setEntryMode] = useState('optimal');
  
  // Get recommended profile from AI
  const recommendedProfile = analysis[`${prefix}recommended_profile`] || 'balanced';
  const marketCondition = analysis[`${prefix}market_condition`] || '';
  
  // Initialize selected profile to AI recommendation
  const [selectedProfile, setSelectedProfile] = useState(recommendedProfile);
  
  // Get the current profile data
  const profileKey = `${prefix}${selectedProfile}`;
  const proposal = analysis[profileKey];
  const profileConfig = PROFILES[selectedProfile];
  const ProfileIcon = profileConfig.icon;
  
  // Get current market price from analysis
  const currentPrice = analysis.current_price || proposal?.entry_price;
  
  // =====================================================
  // LEVIER PAR PROFIL - Chaque profil a son propre levier
  // Prudent: 5x, Équilibré: 10x, Dynamique: 20x
  // =====================================================
  const profileLeverage = proposal?.leverage || { prudent: 5, balanced: 10, dynamic: 20 }[selectedProfile] || 10;
  
  // Editable parameters for recalculation
  const [customLeverage, setCustomLeverage] = useState(profileLeverage);
  const [customCapital, setCustomCapital] = useState(initialCapital);
  const [customRisk, setCustomRisk] = useState(initialRisk);

  // Broker fees pref (used both on initial proposal and on any custom recalculation)
  const { fees: estimatedFeesPct } = useEstimatedFees();
  
  // Update leverage when profile changes
  useEffect(() => {
    const newLeverage = proposal?.leverage || { prudent: 5, balanced: 10, dynamic: 20 }[selectedProfile] || 10;
    setCustomLeverage(newLeverage);
  }, [selectedProfile, proposal]);
  
  // Track if user modified values
  const isModified = customLeverage !== profileLeverage || customCapital !== initialCapital || customRisk !== initialRisk;
  
  // Reset to original values
  const resetToDefaults = () => {
    setCustomLeverage(profileLeverage);
    setCustomCapital(initialCapital);
    setCustomRisk(initialRisk);
  };
  
  // Check if current profile is recommended
  const isRecommended = selectedProfile === recommendedProfile;
  
  // Get the recommended kill zone for intraday
  const getRecommendedKillZone = () => {
    if (isSwing || !analysis.kill_zones || !analysis.intraday_session) return null;
    const sessionMap = {
      'London': 'london',
      'NY AM': 'ny_am',
      'NY PM': 'ny_pm',
      'Asia': 'asia'
    };
    const key = sessionMap[analysis.intraday_session];
    return key ? analysis.kill_zones[key] : null;
  };
  
  const recommendedKZ = getRecommendedKillZone();
  
  // Get base values from proposal
  const optimalEntry = proposal?.entry_price;
  const sl = proposal?.stop_loss;
  const tp1 = proposal?.take_profit_1;
  const tp2 = proposal?.take_profit_2;
  const tp100 = proposal?.take_profit_100;
  
  // Calculate entry based on mode
  const entry = entryMode === 'market' ? currentPrice : optimalEntry;
  
  // Calculate ±2% zone around optimal entry
  const entryZoneLow = optimalEntry ? optimalEntry * 0.98 : null;
  const entryZoneHigh = optimalEntry ? optimalEntry * 1.02 : null;
  
  // Check if current price is in the acceptable entry zone
  const isPriceInZone = currentPrice && entryZoneLow && entryZoneHigh && 
    currentPrice >= entryZoneLow && currentPrice <= entryZoneHigh;
  
  // Calculate R:R for market entry (classic distance-based ratio)
  const calculateMarketRR = () => {
    if (!currentPrice || !sl || !tp100) return null;
    const slDistance = Math.abs(currentPrice - sl);
    const tpDistance = Math.abs(tp100 - currentPrice);
    return slDistance > 0 ? tpDistance / slDistance : 0;
  };
  
  const marketRR = calculateMarketRR();
  const optimalRR = proposal?.risk_reward_ratio;
  
  // Calculate distance from market entry to SL (as percentage)
  // Used to warn when SL is too close to entry (high risk of being stopped out)
  const slDistancePercent = currentPrice && sl ? 
    (Math.abs(currentPrice - sl) / currentPrice) * 100 : null;
  const isSLTooClose = entryMode === 'market' && slDistancePercent !== null && slDistancePercent < 1.5;
  
  // RECALCULATE based on custom parameters - MARGE FIXÉE
  const calculatePnL = () => {
    if (!entry || !sl || !tp100) return null;
    
    // =====================================================
    // LOGIQUE MODE ISOLÉ - MARGE FIXÉE
    // La MARGE est fixée = ce qu'on engage = perte MAX si liquidé
    // La perte au SL est INFÉRIEURE à la marge (car SL avant liquidation)
    // =====================================================
    
    // Marge = Capital × %Risque (ce qu'on engage)
    const marge = customCapital * (customRisk / 100);
    
    // Position = Marge × Levier
    const taillePosition = marge * customLeverage;
    
    // Quantité = Position / Entry
    const quantiteCoin = taillePosition / entry;
    
    let risqueParUnite, gainParUniteTp1, gainParUniteTp2, gainParUniteTp100, liqPriceCalc;
    
    if (isLong) {
      risqueParUnite = Math.abs(entry - sl);
      gainParUniteTp1 = tp1 - entry;
      gainParUniteTp2 = tp2 - entry;
      gainParUniteTp100 = tp100 - entry;
      liqPriceCalc = customLeverage > 1 ? entry * (1 - 1/customLeverage) : 0;
    } else {
      risqueParUnite = Math.abs(sl - entry);
      gainParUniteTp1 = entry - tp1;
      gainParUniteTp2 = entry - tp2;
      gainParUniteTp100 = entry - tp100;
      liqPriceCalc = customLeverage > 1 ? entry * (1 + 1/customLeverage) : 0;
    }
    
    // Perte au SL = Quantité × Distance SL (MOINS que la marge car SL avant liquidation)
    const perteSL = quantiteCoin * risqueParUnite;
    
    // Perte si liquidé = Marge totale
    const perteLiquidation = marge;
    
    // Gains PARTIELS aux différents TPs (selon la stratégie de sortie)
    // TP1 = fermer 25% de la position
    // TP2 = fermer 25% de la position (50% total)
    // TP100 = fermer 50% restants (100% total)
    const gainTp1Gross = quantiteCoin * 0.25 * gainParUniteTp1;  // 25% de position
    const gainTp2Gross = quantiteCoin * 0.25 * gainParUniteTp2;  // 25% de position
    const gainTp100Gross = quantiteCoin * 0.50 * gainParUniteTp100;  // 50% restants

    // ==== FRAIS BROKER (round-trip sur notional) ====
    // Ex: 0.30% × 2 × 100 000$ notional = 600$ de frais par trade round-trip
    const feesPct = Number(estimatedFeesPct) || 0;
    const feesTotal = 2 * (feesPct / 100) * taillePosition;

    // Répartition des frais sur les 3 sorties (TP1=25% du volume, TP2=25%, TP100=50%)
    const feesTp1 = feesTotal * 0.25;
    const feesTp2 = feesTotal * 0.25;
    const feesTp100 = feesTotal * 0.50;

    const gainTp1 = gainTp1Gross - feesTp1;
    const gainTp2 = gainTp2Gross - feesTp2;
    const gainTp100 = gainTp100Gross - feesTp100;
    const perteSLNet = perteSL + feesTotal;

    // Gain TOTAL si tous les TPs sont atteints (NET après frais)
    const gainTotal = gainTp1 + gainTp2 + gainTp100;

    // R:R = Distance TP100 / Distance SL (ratio classique en trading)
    const tpDistance = Math.abs(tp100 - entry);
    const slDistance = Math.abs(entry - sl);
    const rrCalc = slDistance > 0 ? tpDistance / slDistance : 0;

    // ROI basé sur la marge (NET après frais)
    const roiPerte = marge > 0 ? (perteSLNet / marge) * 100 : 0;
    const roiGain = marge > 0 ? (gainTp100 / marge) * 100 : 0;

    return {
      posSize: quantiteCoin,
      posValue: marge,
      effPos: taillePosition,
      liqPrice: liqPriceCalc,
      maxLoss: perteSLNet,           // Perte au SL NETTE (frais inclus)
      perteLiquidation: perteLiquidation + feesTotal,
      profitTp1: gainTp1,            // NET
      profitTp2: gainTp2,            // NET
      profitTp100: gainTp100,        // NET
      profitTotal: gainTotal,        // NET total
      rr: rrCalc,
      pnlSl: -roiPerte,
      pnlTp100: roiGain,
      riskPerUnit: risqueParUnite,
      gainPerUnit: gainParUniteTp100,
      feesTotal: feesTotal,          // Frais round-trip appliqués (info UI)
      feesPercent: feesPct,          // % de frais par jambe (info UI)
    };
  };
  
  // Get calculated values (use custom or original)
  const calc = calculatePnL();
  const posSize = calc?.posSize ?? proposal?.position_size;
  const posValue = calc?.posValue ?? proposal?.position_value;
  const effPos = calc?.effPos ?? proposal?.effective_position;
  const liqPrice = calc?.liqPrice ?? proposal?.liquidation_price;
  const maxLoss = calc?.maxLoss ?? proposal?.max_loss;
  const profitTp1 = calc?.profitTp1 ?? proposal?.profit_tp1;
  const profitTp2 = calc?.profitTp2 ?? proposal?.profit_tp2;
  const profitTp100 = calc?.profitTp100 ?? proposal?.profit_tp100;
  const profitTotal = calc?.profitTotal ?? (profitTp1 + profitTp2 + profitTp100);
  const rr = calc?.rr ?? proposal?.risk_reward_ratio;
  const pnlSl = calc?.pnlSl ?? proposal?.pnl_percentage_sl;
  const pnlTp100 = calc?.pnlTp100 ?? proposal?.pnl_percentage_tp100;
  
  // Calculate per unit values
  const riskPerUnit = entry && sl ? Math.abs(entry - sl) : 0;
  const gainPerUnit = entry && tp100 ? Math.abs(tp100 - entry) : 0;
  
  // Build complete trade notes with ICT analysis
  const buildTradeNotes = () => {
    const parts = [];
    
    // Profile
    parts.push(`📊 PROFIL: ${profileConfig.label[language === 'fr' ? 'fr' : 'en']}`);
    
    // Market Structure
    if (analysis.market_structure) {
      parts.push(`\n📈 STRUCTURE DE MARCHÉ:\n${analysis.market_structure}`);
    }
    
    // ICT Analysis
    const ictAnalysis = analysis[`${prefix}analysis`];
    if (ictAnalysis) {
      parts.push(`\n🎯 ANALYSE ICT (${isSwing ? 'SWING' : 'INTRADAY'}):\n${ictAnalysis}`);
    }
    
    // Key levels identified
    if (analysis.key_levels) {
      parts.push(`\n📍 NIVEAUX CLÉS:\n${analysis.key_levels}`);
    }
    
    // R:R info
    if (rr) {
      parts.push(`\n⚖️ R:R: ${rr.toFixed(2)}:1`);
    }
    
    return parts.join('\n');
  };
  
  // Save trade to Action
  const handleSaveTrade = async (isPaper = false) => {
    setSaving(true);
    try {
      // Use marge (posValue) as position_size, not quantity
      const margeToSave = calc?.posValue ?? (customCapital * customRisk / 100);
      
      // Si entrée au marché, le trade est directement actif
      const tradeStatus = entryMode === 'market' ? 'active' : 'pending';
      
      await onSaveTrade({
        symbol: symbol,
        direction: dir,
        trade_type: isSwing ? 'SWING' : 'INTRADAY',
        entry_price: entry,
        stop_loss: sl,
        take_profit_1: tp1,
        take_profit_2: tp2,
        take_profit_100: tp100,
        leverage: customLeverage,
        position_size: margeToSave,  // Marge en $, pas quantité de coins
        notes: buildTradeNotes(),
        status: tradeStatus,
        is_paper: isPaper,
        profile: selectedProfile,
      });
      if (isPaper) {
        toast.success(language === 'fr' ? 'Trade Paper sauvegardé !' : 'Paper trade saved!');
      } else {
        toast.success(language === 'fr' 
          ? `Trade réel ${tradeStatus === 'active' ? 'lancé' : 'sauvegardé'} !` 
          : `Real trade ${tradeStatus === 'active' ? 'started' : 'saved'}!`);
      }
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur de sauvegarde' : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {isSwing ? <Calendar className="w-5 h-5 text-blue-400" /> : <Clock className="w-5 h-5 text-orange-400" />}
          <span className="font-bold">{isSwing ? 'SWING' : 'INTRADAY'}</span>
          {!isSwing && analysis.intraday_session && (
            <span className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 text-xs font-mono">
              {analysis.intraday_session}
            </span>
          )}
        </div>
        <div className={`px-4 py-2 rounded-lg font-mono font-bold ${isLong ? 'bg-primary/20 text-primary' : 'bg-destructive/20 text-destructive'}`}>
          {isLong ? <TrendingUp className="w-5 h-5 inline mr-1" /> : <TrendingDown className="w-5 h-5 inline mr-1" />}
          {dir}
        </div>
      </div>

      {/* TRADE PARAMETERS - Always visible at the top */}
      <div className="p-4 rounded-xl bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4 text-cyan-400" />
            {language === 'fr' ? 'Paramètres du Trade' : 'Trade Parameters'}
            {isModified && (
              <span className="px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400 text-xs font-mono animate-pulse">
                {language === 'fr' ? 'Modifié' : 'Modified'}
              </span>
            )}
          </div>
          {isModified && (
            <Button
              variant="ghost"
              size="sm"
              onClick={resetToDefaults}
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              data-testid={`reset-params-${type}`}
            >
              <RotateCcw className="w-3 h-3 mr-1" />
              {language === 'fr' ? 'Réinitialiser' : 'Reset'}
            </Button>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Leverage Control */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <Zap className="w-3 h-3 text-yellow-400" />
                {language === 'fr' ? 'Levier' : 'Leverage'}
              </Label>
              <div className="flex items-center gap-1">
                <Input
                  type="number"
                  min="1"
                  max="200"
                  value={customLeverage}
                  onChange={(e) => setCustomLeverage(Math.max(1, Math.min(200, parseInt(e.target.value) || 1)))}
                  className="w-16 h-7 text-center font-mono text-sm bg-secondary/50 border-white/10"
                  data-testid={`leverage-input-${type}`}
                />
                <span className="text-xs text-muted-foreground">x</span>
              </div>
            </div>
            <Slider
              value={[customLeverage]}
              onValueChange={([val]) => setCustomLeverage(val)}
              min={1}
              max={200}
              step={1}
              className="w-full"
              data-testid={`leverage-slider-${type}`}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>1x</span>
              <span className={customLeverage > 50 ? 'text-yellow-400 font-bold' : ''}>
                {customLeverage > 100 ? '⚠️' : ''}{customLeverage}x
              </span>
              <span>200x</span>
            </div>
          </div>
          
          {/* Capital Control */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <DollarSign className="w-3 h-3 text-green-400" />
                {language === 'fr' ? 'Capital' : 'Capital'}
              </Label>
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground">$</span>
                <Input
                  type="number"
                  min="100"
                  max="1000000"
                  value={customCapital}
                  onChange={(e) => setCustomCapital(Math.max(100, parseFloat(e.target.value) || 100))}
                  className="w-24 h-7 text-center font-mono text-sm bg-secondary/50 border-white/10"
                  data-testid={`capital-input-${type}`}
                />
              </div>
            </div>
            <Slider
              value={[Math.min(customCapital, 100000)]}
              onValueChange={([val]) => setCustomCapital(val)}
              min={100}
              max={100000}
              step={100}
              className="w-full"
              data-testid={`capital-slider-${type}`}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>$100</span>
              <span>${customCapital.toLocaleString()}</span>
              <span>$100k</span>
            </div>
          </div>
          
          {/* Risk Percentage Control */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <Percent className="w-3 h-3 text-red-400" />
                {language === 'fr' ? 'Risque' : 'Risk'}
              </Label>
              <div className="flex items-center gap-1">
                <Input
                  type="number"
                  min="0.5"
                  max="10"
                  step="0.5"
                  value={customRisk}
                  onChange={(e) => setCustomRisk(Math.max(0.5, Math.min(10, parseFloat(e.target.value) || 1)))}
                  className="w-16 h-7 text-center font-mono text-sm bg-secondary/50 border-white/10"
                  data-testid={`risk-input-${type}`}
                />
                <span className="text-xs text-muted-foreground">%</span>
              </div>
            </div>
            <Slider
              value={[customRisk]}
              onValueChange={([val]) => setCustomRisk(val)}
              min={0.5}
              max={10}
              step={0.5}
              className="w-full"
              data-testid={`risk-slider-${type}`}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>0.5%</span>
              <span className={customRisk > 3 ? 'text-yellow-400 font-bold' : ''}>
                {customRisk > 5 ? '⚠️' : ''}{customRisk}%
              </span>
              <span>10%</span>
            </div>
          </div>
        </div>
        
        {/* Quick summary of max loss */}
        <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{language === 'fr' ? 'Perte Max Autorisée' : 'Max Loss Allowed'}:</span>
          <span className="font-mono font-bold text-destructive">${(customCapital * customRisk / 100).toFixed(2)}</span>
        </div>
      </div>
      
      {/* Profile Selector */}
      <div className={`p-4 rounded-xl border ${profileConfig.borderColor} ${profileConfig.bgColor}`}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <ProfileIcon className={`w-5 h-5 ${profileConfig.color}`} />
            <span className="font-medium">{language === 'fr' ? 'Profil de risque' : 'Risk Profile'}</span>
            {isRecommended && (
              <span className="px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400 text-xs font-bold flex items-center gap-1 animate-pulse">
                <CheckCircle className="w-3 h-3" />
                {language === 'fr' ? 'Recommandé IA' : 'AI Recommended'}
              </span>
            )}
          </div>
          <Select value={selectedProfile} onValueChange={setSelectedProfile}>
            <SelectTrigger className="w-[220px] bg-secondary/50 border-white/10" data-testid="profile-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.values(PROFILES).map((profile) => {
                const Icon = profile.icon;
                const isProfileRecommended = profile.key === recommendedProfile;
                return (
                  <SelectItem key={profile.key} value={profile.key}>
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${profile.color}`} />
                      <span>{profile.label[language === 'fr' ? 'fr' : 'en']}</span>
                      {isProfileRecommended && (
                        <span className="ml-1 text-xs text-cyan-400">★</span>
                      )}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          {profileConfig.description[language === 'fr' ? 'fr' : 'en']}
        </p>
        
        {/* Market Condition - AI Recommendation Reason */}
        {marketCondition && (
          <div className="mt-3 p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
            <div className="flex items-start gap-2">
              <Zap className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-xs font-medium text-cyan-400">
                  {language === 'fr' ? 'Analyse IA du marché' : 'AI Market Analysis'}:
                </span>
                <p className="text-xs text-muted-foreground mt-0.5">{marketCondition}</p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Entry Mode Selector - Optimal vs Market */}
      <div className="p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-medium">{language === 'fr' ? 'Point d\'entrée' : 'Entry Point'}</span>
          </div>
          <div className="flex items-center gap-2 bg-secondary/50 rounded-lg p-1">
            <button
              onClick={() => setEntryMode('optimal')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                entryMode === 'optimal' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {language === 'fr' ? 'Entrée IA' : 'AI Entry'}
            </button>
            <button
              onClick={() => setEntryMode('market')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                entryMode === 'market' 
                  ? 'bg-orange-500 text-white' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {language === 'fr' ? 'Au marché' : 'Market Price'}
            </button>
          </div>
        </div>
        
        {/* Entry Zone Display */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* Optimal Entry with ±2% Zone */}
          <div className={`p-3 rounded-lg ${entryMode === 'optimal' ? 'bg-primary/20 border border-primary/30' : 'bg-secondary/30'}`}>
            <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
              <Target className="w-3 h-3" />
              {language === 'fr' ? 'Entrée Optimale (IA)' : 'Optimal Entry (AI)'}
            </div>
            <div className="font-mono font-bold text-lg">${optimalEntry?.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground mt-1">
              {language === 'fr' ? 'Zone ±2%' : '±2% Zone'}: <span className="font-mono">${entryZoneLow?.toLocaleString()} - ${entryZoneHigh?.toLocaleString()}</span>
            </div>
          </div>
          
          {/* Current Market Price */}
          <div className={`p-3 rounded-lg ${entryMode === 'market' ? 'bg-orange-500/20 border border-orange-500/30' : 'bg-secondary/30'}`}>
            <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              {language === 'fr' ? 'Cours Actuel' : 'Current Price'}
            </div>
            <div className="font-mono font-bold text-lg">${currentPrice?.toLocaleString()}</div>
            {isPriceInZone ? (
              <div className="text-xs text-primary mt-1 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                {language === 'fr' ? 'Dans la zone d\'entrée' : 'In entry zone'}
              </div>
            ) : (
              <div className="text-xs text-yellow-400 mt-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {language === 'fr' ? 'Hors zone optimale' : 'Outside optimal zone'}
              </div>
            )}
          </div>
          
          {/* R:R Comparison */}
          <div className="p-3 rounded-lg bg-secondary/30">
            <div className="text-xs text-muted-foreground mb-1">{language === 'fr' ? 'Ratio R:R' : 'R:R Ratio'}</div>
            <div className="flex items-center gap-2">
              <div className={`font-mono font-bold text-lg ${entryMode === 'optimal' ? 'text-primary' : 'text-orange-400'}`}>
                {entryMode === 'optimal' ? optimalRR?.toFixed(2) : marketRR?.toFixed(2)}:1
              </div>
              {entryMode === 'market' && marketRR && optimalRR && (
                <span className={`text-xs px-1.5 py-0.5 rounded ${marketRR > optimalRR ? 'bg-primary/20 text-primary' : 'bg-destructive/20 text-destructive'}`}>
                  {marketRR > optimalRR ? '↑' : '↓'} vs IA
                </span>
              )}
            </div>
          </div>
        </div>
        
        {/* Warning for Market Entry - Price outside zone */}
        {entryMode === 'market' && !isPriceInZone && (
          <div className="mt-3 p-2 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-yellow-400">
                {language === 'fr' 
                  ? 'Attention : Le cours actuel est hors de la zone d\'entrée optimale (±2%). L\'IA recommande d\'attendre un meilleur point d\'entrée pour un meilleur ratio risque/rendement.' 
                  : 'Warning: Current price is outside the optimal entry zone (±2%). AI recommends waiting for a better entry point for a better risk/reward ratio.'}
              </p>
            </div>
          </div>
        )}
        
        {/* Warning for Market Entry - R:R too low */}
        {entryMode === 'market' && marketRR !== null && marketRR < 2 && (
          <div className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
            <div className="flex items-start gap-2">
              <XCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-400">
                  {language === 'fr' 
                    ? 'Trade non recommandé' 
                    : 'Trade not recommended'}
                </p>
                <p className="text-xs text-red-400/80 mt-1">
                  {language === 'fr' 
                    ? `Après analyse au marché, aucun trade avec un ratio minimum de 2:1 n'a été trouvé. Le ratio actuel est de ${marketRR.toFixed(2)}:1. Attendez un meilleur point d'entrée ou utilisez l'entrée optimale de l'IA.` 
                    : `After market analysis, no trade with a minimum 2:1 ratio was found. Current ratio is ${marketRR.toFixed(2)}:1. Wait for a better entry point or use the AI's optimal entry.`}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Warning for Market Entry - SL too close (high volatility risk) */}
        {entryMode === 'market' && isSLTooClose && marketRR >= 2 && (
          <div className="mt-3 p-3 rounded-lg bg-orange-500/10 border border-orange-500/30">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-orange-400">
                  {language === 'fr' 
                    ? 'Attention : Stop Loss très proche' 
                    : 'Warning: Stop Loss very close'}
                </p>
                <p className="text-xs text-orange-400/80 mt-1">
                  {language === 'fr' 
                    ? `Le Stop Loss est à seulement ${slDistancePercent?.toFixed(2)}% du prix d'entrée. Une légère volatilité pourrait déclencher le SL prématurément. L'entrée optimale de l'IA offre une meilleure marge de sécurité.` 
                    : `The Stop Loss is only ${slDistancePercent?.toFixed(2)}% from the entry price. Minor volatility could trigger the SL prematurely. The AI's optimal entry offers better safety margin.`}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Recommended Kill Zone for Intraday */}
      {!isSwing && recommendedKZ && (
        <div className={`p-3 rounded-lg border ${recommendedKZ.quality === 'BEST' ? 'bg-green-500/10 border-green-500/30' : recommendedKZ.quality === 'HIGH' ? 'bg-blue-500/10 border-blue-500/30' : 'bg-yellow-500/10 border-yellow-500/30'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-medium">{language === 'fr' ? 'Exécuter pendant' : 'Execute during'}:</span>
              <span className="font-mono font-bold text-purple-400">{recommendedKZ.name}</span>
            </div>
            <span className="font-mono text-lg font-bold">{recommendedKZ.time}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">{recommendedKZ.description}</p>
        </div>
      )}

      {/* Price Levels with DYNAMIC GAINS - Entry, SL, TPs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
        <div className="p-3 rounded-lg bg-secondary/50">
          <div className="text-xs text-muted-foreground">Entry</div>
          <div className="font-mono font-bold">${entry?.toLocaleString()}</div>
        </div>
        <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
          <div className="text-xs text-muted-foreground flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Stop Loss</div>
          <div className="font-mono font-bold text-destructive">${sl?.toLocaleString()}</div>
          <div className="text-xs text-destructive mt-1">-${Math.abs(maxLoss || 0)?.toLocaleString()}</div>
        </div>
        <div className="p-3 rounded-lg bg-primary/5 border border-primary/10">
          <div className="text-xs text-muted-foreground">TP1 (25%)</div>
          <div className="font-mono font-bold text-primary/80">${tp1?.toLocaleString()}</div>
          <div className="text-xs text-primary/80 mt-1">+${(profitTp1 || 0)?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
        </div>
        <div className="p-3 rounded-lg bg-primary/10 border border-primary/15">
          <div className="text-xs text-muted-foreground">TP2 (50%)</div>
          <div className="font-mono font-bold text-primary/90">${tp2?.toLocaleString()}</div>
          <div className="text-xs text-primary/90 mt-1">+${(profitTp2 || 0)?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
        </div>
        <div className="p-3 rounded-lg bg-primary/20 border border-primary/30">
          <div className="text-xs text-muted-foreground">TP100%</div>
          <div className="font-mono font-bold text-primary">${tp100?.toLocaleString()}</div>
          <div className="text-xs text-primary mt-1">+${(profitTp100 || 0)?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
        </div>
        <div className="p-3 rounded-lg bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30">
          <div className="text-xs text-muted-foreground flex items-center gap-1">
            <Trophy className="w-3 h-3 text-green-400" />
            {language === 'fr' ? 'Gain Total' : 'Total Profit'}
          </div>
          <div className="font-mono font-bold text-green-400 text-lg">+${(profitTotal || 0)?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
          <div className="text-[10px] text-green-400/70">{language === 'fr' ? 'Si tous TPs atteints' : 'If all TPs hit'}</div>
        </div>
      </div>

      {/* Position Details - Simplified (controls are now at the top) */}
      <div className="p-4 rounded-xl bg-gradient-to-r from-yellow-500/5 to-orange-500/5 border border-yellow-500/20">
        <div className="text-sm font-medium mb-3 flex items-center gap-2">
          <Zap className="w-4 h-4 text-yellow-400" />
          {language === 'fr' ? 'Détails de Position' : 'Position Details'}
          <span className="px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-400 text-xs font-mono">{customLeverage}x</span>
        </div>
        
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Collateral */}
          <div className="p-3 rounded-lg bg-secondary/30">
            <div className="text-xs text-muted-foreground mb-1">{language === 'fr' ? 'Collatéral' : 'Collateral'}</div>
            <div className="font-mono font-bold">${posValue?.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground">= {language === 'fr' ? 'Marge' : 'Margin'}</div>
          </div>
          
          {/* Position Size */}
          <div className="p-3 rounded-lg bg-yellow-500/10">
            <div className="text-xs text-muted-foreground mb-1">{language === 'fr' ? 'Taille Position' : 'Position Size'}</div>
            <div className="font-mono font-bold text-yellow-400">${effPos?.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground">× {customLeverage} {language === 'fr' ? 'levier' : 'leverage'}</div>
          </div>
          
          {/* Quantity */}
          <div className="p-3 rounded-lg bg-primary/10">
            <div className="text-xs text-muted-foreground mb-1">{language === 'fr' ? 'Quantité COIN' : 'COIN Quantity'}</div>
            <div className="font-mono font-bold text-primary">{posSize?.toFixed(6)}</div>
          </div>
          
          {/* Liquidation */}
          {customLeverage > 1 && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 text-red-400" />
                Liquidation
              </div>
              <div className="font-mono font-bold text-red-400">${liqPrice?.toLocaleString()}</div>
              <div className="text-xs text-red-400/80 font-mono">-${posValue?.toLocaleString()}</div>
            </div>
          )}
        </div>
      </div>

      {/* Risk & Reward Summary */}
      <div className="grid grid-cols-2 gap-3">
        {/* Loss Side */}
        <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
          <div className="text-sm font-medium mb-2 text-destructive">{language === 'fr' ? 'Risque (SL)' : 'Risk (SL)'}</div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-xs text-muted-foreground">{language === 'fr' ? 'Perte $' : 'Loss $'}</span>
              <span className="font-mono font-bold text-destructive">-${Math.abs(maxLoss || 0)?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-muted-foreground">ROI</span>
              <span className="font-mono text-destructive">{pnlSl?.toFixed(2) || 0}%</span>
            </div>
          </div>
        </div>
        
        {/* Gain Side */}
        <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
          <div className="text-sm font-medium mb-2 text-green-400">{language === 'fr' ? 'Gain Total' : 'Total Profit'}</div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-xs text-muted-foreground">{language === 'fr' ? 'Gain $' : 'Profit $'}</span>
              <span className="font-mono font-bold text-green-400">+${(profitTotal || 0)?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-muted-foreground">ROI</span>
              <span className="font-mono text-green-400">+{(posValue > 0 ? (profitTotal / posValue) * 100 : 0).toFixed(2)}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* R:R Ratio */}
      <div className={`p-3 rounded-lg text-center ${profileConfig.bgColor} border ${profileConfig.borderColor}`}>
        <div className="text-xs text-muted-foreground">Ratio R:R</div>
        <div className={`font-mono text-2xl font-bold ${profileConfig.color}`}>{rr?.toFixed(2) || 0}:1</div>
      </div>

      {/* Broker fees info line — transparency about what was deducted */}
      {calc?.feesTotal > 0 && (
        <div
          data-testid="fees-info-line"
          className="flex items-center justify-between p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 text-xs"
        >
          <span className="text-amber-400/90">
            {language === 'fr'
              ? `Frais broker (${(calc.feesPercent || 0).toFixed(2)}% × 2 jambes, déjà déduits)`
              : `Broker fees (${(calc.feesPercent || 0).toFixed(2)}% × 2 legs, already deducted)`}
          </span>
          <span className="font-mono font-bold text-amber-400">
            −${(calc.feesTotal || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </span>
        </div>
      )}

      {/* Analysis */}
      <div className="p-3 rounded-lg bg-secondary/30">
        <div className="text-xs text-muted-foreground mb-1">{language === 'fr' ? 'Analyse ICT' : 'ICT Analysis'}</div>
        <p className="text-sm">{analysis[`${prefix}analysis`]}</p>
      </div>

      {/* Save Trade Section */}
      <div className="space-y-2">
        <div className="text-sm font-semibold text-center text-muted-foreground">
          {language === 'fr' ? 'Sauvegarder le trade' : 'Save Trade'}
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => handleSaveTrade(false)} 
            disabled={saving}
            className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black font-bold"
            data-testid={`save-trade-real-${type}`}
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {language === 'fr' ? 'Réel' : 'Real'}
          </Button>
          <Button 
            onClick={() => handleSaveTrade(true)} 
            disabled={saving}
            variant="outline"
            className="flex-1 border-purple-500/50 text-purple-400 hover:bg-purple-500/20 font-bold"
            data-testid={`save-trade-paper-${type}`}
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-2" />
            )}
            {language === 'fr' ? 'Paper' : 'Paper'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { t, language } = useLanguage();
  const { isAuthenticated, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [charts, setCharts] = useState({ m15: null, hourly: null, daily: null, weekly: null });
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [prices, setPrices] = useState([]);
  const [loadingPrices, setLoadingPrices] = useState(true);
  const [formData, setFormData] = useState({ symbol: 'BTC/USDT', capital: 10000, risk: 1, leverage: 10 });
  const [watchlist, setWatchlist] = useState([]);
  const [showSymbolDropdown, setShowSymbolDropdown] = useState(false);
  const [generatingCharts, setGeneratingCharts] = useState(false);
  
  // State for chart zoom modal
  const [selectedChart, setSelectedChart] = useState(null); // { src, label, timeframe }

  // Fetch watchlist for symbol selection
  const fetchWatchlist = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/watchlist/symbols`);
      setWatchlist(response.data.symbols || []);
    } catch (error) {
      console.error('Failed to fetch watchlist:', error);
    }
  }, []);

  const fetchPrices = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/crypto/prices`);
      setPrices(response.data.prices);
    } catch (error) { console.error('Failed to fetch prices:', error); }
    finally { setLoadingPrices(false); }
  }, []);

  useEffect(() => {
    fetchPrices();
    fetchWatchlist();
    const interval = setInterval(fetchPrices, 30000);
    return () => clearInterval(interval);
  }, [fetchPrices, fetchWatchlist]);

  const hasAnyChart = charts.m15 || charts.hourly || charts.daily || charts.weekly;
  const chartCount = [charts.m15, charts.hourly, charts.daily, charts.weekly].filter(Boolean).length;

  // Save trade to Action
  const saveTrade = async (tradeData) => {
    await axios.post(`${API}/trades`, tradeData);
  };

  // Generate charts automatically for all 4 timeframes
  const analyzeAutoCharts = async () => {
    if (!formData.symbol) {
      toast.error(language === 'fr' ? 'Entrez un symbole' : 'Enter a symbol');
      return;
    }
    
    setGeneratingCharts(true);
    setAnalyzing(false);
    setAnalysis(null);
    
    try {
      // Generate all 4 timeframes
      const generateToast = toast.loading(language === 'fr' ? 'Génération des 4 graphiques (15m, 1H, 1D, 1W)...' : 'Generating 4 charts (15m, 1H, 1D, 1W)...');
      
      const chartsResponse = await axios.post(`${API}/charts/generate`, {
        symbol: formData.symbol,
        timeframes: ["15m", "1H", "1D", "1W"]
      }, { timeout: 120000 }); // 2 min timeout for 4 charts
      
      toast.dismiss(generateToast);
      
      const generatedCharts = chartsResponse.data.charts;
      
      if (!generatedCharts || Object.keys(generatedCharts).length === 0) {
        toast.error(language === 'fr' ? 'Impossible de générer les graphiques' : 'Failed to generate charts');
        setGeneratingCharts(false);
        return;
      }
      
      // Update all chart previews
      const newCharts = {
        m15: generatedCharts["15m"] ? `data:image/png;base64,${generatedCharts["15m"].image_base64}` : null,
        hourly: generatedCharts["1H"] ? `data:image/png;base64,${generatedCharts["1H"].image_base64}` : null,
        daily: generatedCharts["1D"] ? `data:image/png;base64,${generatedCharts["1D"].image_base64}` : null,
        weekly: generatedCharts["1W"] ? `data:image/png;base64,${generatedCharts["1W"].image_base64}` : null,
      };
      setCharts(newCharts);

      const chartCountGenerated = Object.values(newCharts).filter(Boolean).length;
      setGeneratingCharts(false);

      // Surface per-timeframe warnings so the user knows which ones fell back or failed
      const chartErrors = chartsResponse.data?.errors || [];
      if (chartCountGenerated > 0 && chartErrors.length > 0) {
        const missing = chartErrors
          .map((e) => (typeof e === 'string' ? e.split(':')[0] : ''))
          .filter(Boolean)
          .join(', ');
        toast.warning(
          language === 'fr'
            ? `${chartCountGenerated}/4 graphiques générés — ${missing} indisponible(s) (marché fermé, week-end ou actif sans historique intraday). L'analyse IA reste possible avec les autres.`
            : `${chartCountGenerated}/4 charts generated — ${missing} unavailable (market closed, weekend, or asset without intraday history). AI analysis still works with the others.`,
          { duration: 6000 }
        );
      } else if (chartCountGenerated > 0) {
        toast.success(language === 'fr'
          ? `${chartCountGenerated} graphiques générés ! Cliquez sur Analyser.`
          : `${chartCountGenerated} charts generated! Click Analyze.`);
      }
      
    } catch (error) {
      toast.dismiss();
      const errorMsg = error.response?.data?.detail || error.message || 'Failed';
      toast.error(language === 'fr' ? `Erreur: ${errorMsg}` : `Error: ${errorMsg}`);
      setGeneratingCharts(false);
    }
  };

  const analyzeChart = async () => {
    if (!hasAnyChart) {
      toast.error(language === 'fr'
        ? 'Générez d\'abord les graphiques (bouton "Générer les 4 graphiques")'
        : 'Generate the charts first (click "Generate 4 charts")');
      return;
    }
    setAnalyzing(true); setAnalysis(null);
    try {
      const getBase64 = (dataUrl) => dataUrl ? dataUrl.split(',')[1] : null;
      
      // Determine primary chart (use hourly if available, else daily, else weekly)
      const primaryChart = charts.hourly || charts.daily || charts.weekly;
      
      const payload = {
        image_base64: getBase64(primaryChart),
        image_base64_daily: charts.hourly ? getBase64(charts.daily) : null,
        image_base64_weekly: (charts.hourly || charts.daily) ? getBase64(charts.weekly) : null,
        image_15m_b64: getBase64(charts.m15),  // 15m chart for intraday precision
        symbol: formData.symbol, 
        timeframe: charts.hourly ? '1H' : charts.daily ? '1D' : '1W',
        capital: formData.capital, 
        risk_percentage: formData.risk, 
        leverage: formData.leverage,
        user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
      
      // Show loading toast for long operation
      const loadingToast = toast.loading(language === 'fr' ? 'Analyse IA en cours... (30-60 sec)' : 'AI Analysis in progress... (30-60 sec)');
      
      try {
        // Async job + polling (avoids production proxy 504 timeouts)
        const startRes = await axios.post(`${API}/analyze/chart-async`, payload, { timeout: 60000 });
        const jobId = startRes.data.job_id;

        // Cache hit: the analysis is already ready — notify the user gracefully
        if (startRes.data?.from_cache) {
          const minsLeft = startRes.data.cache_remaining_minutes || 10;
          toast.info(
            language === 'fr'
              ? `Les caractéristiques du graphique n'ont pas évolué depuis votre dernière analyse. Vous pourrez en refaire une nouvelle dans ${minsLeft} min.`
              : `Chart characteristics haven't changed since your last analysis. You can run a new one in ${minsLeft} min.`,
            { duration: 6000 }
          );
        }

        let result = null;
        const maxAttempts = 60; // 60 x 3s = 3 min max
        for (let i = 0; i < maxAttempts; i++) {
          // If cache hit, the job is already completed — no need to wait 3s
          if (i > 0 || !startRes.data?.from_cache) {
            await new Promise(r => setTimeout(r, 3000));
          }
          const statusRes = await axios.get(`${API}/analyze/chart-status/${jobId}`);
          if (statusRes.data.status === 'completed') {
            result = statusRes.data.result;
            break;
          }
          if (statusRes.data.status === 'failed') {
            throw new Error(statusRes.data.error || 'Analysis failed');
          }
        }
        
        if (!result) {
          throw new Error(language === 'fr' ? "L'analyse a pris trop de temps, réessayez" : 'Analysis timed out, please retry');
        }
        
        setAnalysis(result);
        if (!startRes.data?.from_cache) {
          toast.success(language === 'fr' ? 'Analyse ICT terminée !' : 'ICT Analysis complete!');
        }
      } finally {
        toast.dismiss(loadingToast);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Analysis failed';
      toast.error(language === 'fr' ? `Erreur: ${errorMsg}` : `Error: ${errorMsg}`);
    }
    finally { setAnalyzing(false); }
  };

  // Handle analyze button click - check for free analyses or redirect to subscribe
  const handleAnalyzeClick = async () => {
    if (!isAuthenticated) {
      navigate('/subscribe');
      return;
    }
    
    // Check if user has free analyses
    const freeAnalyses = user?.free_analyses || { swing: 0, intraday: 0 };
    const hasSwing = freeAnalyses.swing > 0;
    const hasIntraday = freeAnalyses.intraday > 0;
    
    if (hasSwing || hasIntraday) {
      // Determine type based on charts uploaded (15m = intraday, else swing)
      const isIntradayAnalysis = charts.m15 !== null;
      const analysisType = isIntradayAnalysis && hasIntraday ? 'intraday' : hasSwing ? 'swing' : 'intraday';
      
      try {
        // Use the free credit via API
        const response = await axios.post(`${API}/auth/use-free-analysis?analysis_type=${analysisType}`);
        toast.success(language === 'fr' 
          ? `Analyse ${analysisType} gratuite utilisée ! (${response.data.total_remaining} restante${response.data.total_remaining > 1 ? 's' : ''})` 
          : `Free ${analysisType} analysis used! (${response.data.total_remaining} remaining)`);
        // Refresh user data to update free_analyses count
        refreshUser();
        // Proceed with analysis
        analyzeChart();
      } catch (error) {
        // No more free, redirect to subscribe
        navigate('/subscribe');
      }
    } else {
      // No free analyses, redirect to subscribe
      navigate('/subscribe');
    }
  };

  // Legal disclaimer hook
  const { showDisclaimer, setShowDisclaimer, checkDisclaimer, handleAccept: handleDisclaimerAccept } = useDisclaimerCheck();

  return (
    <div className="min-h-[calc(100vh-64px)] p-4 sm:p-6 lg:p-8">
      {/* Onboarding Tutorial */}
      <OnboardingTutorial
        pageId="analysis"
        slides={ANALYSIS_SLIDES}
        storageKey="tutorial_analysis_page"
      />

      {/* Legal Disclaimer (shown before first trade) */}
      <LegalDisclaimer
        isOpen={showDisclaimer}
        onClose={() => setShowDisclaimer(false)}
        onAccept={handleDisclaimerAccept}
        language={language}
      />

      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-bold">{t('dashboard.title')}</h1>
              <HowItWorks pageKey="analysis" />
            </div>
            <p className="text-muted-foreground mt-1">{language === 'fr' ? 'Analyse ICT Multi-Timeframe (Order Blocks, FVG, Liquidité, Structure de Marché)' : 'ICT Multi-Timeframe Analysis (Order Blocks, FVG, Liquidity, Market Structure)'}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <EstimatedFeesField data-testid="dashboard-fees-field" />
            <TradingViewLink symbol={formData.symbol} label="TradingView" />
          </div>
        </div>

        {/* Live Prices */}
        <Card className="bg-card border-white/10 overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-center gap-4 overflow-x-auto pb-2">
              <div className="flex items-center gap-2 text-muted-foreground shrink-0">
                <DollarSign className="w-4 h-4" /><span className="text-sm font-mono">LIVE</span>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={fetchPrices}><RefreshCw className={`w-3 h-3 ${loadingPrices ? 'animate-spin' : ''}`} /></Button>
              </div>
              {prices.slice(0, 8).map((coin) => (
                <div key={coin.id} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-secondary/50 shrink-0">
                  <img src={coin.image} alt={coin.name} className="w-5 h-5 rounded-full" />
                  <span className="font-mono font-bold text-sm">{coin.symbol}</span>
                  <span className="font-mono text-sm">${coin.current_price?.toLocaleString()}</span>
                  <span className={`font-mono text-xs ${coin.price_change_24h >= 0 ? 'text-primary' : 'text-destructive'}`}>{coin.price_change_24h >= 0 ? '+' : ''}{coin.price_change_24h?.toFixed(2)}%</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Main Section */}
          <div className="lg:col-span-8 space-y-6">
            {/* Auto-generated MTF Charts (manual upload retired 2026-02 — see comment
                on ChartUpload at top of file). The AI now always reads a standardized
                chart produced by the backend to guarantee consistent, reproducible reads. */}
            <Card className="bg-card border-white/10">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <CardTitle className="flex items-center gap-2">
                    <Layers className="w-5 h-5 text-primary" />
                    {language === 'fr' ? 'Graphiques MTF' : 'MTF Charts'}
                    <span className="ml-2 px-2 py-0.5 rounded-full bg-primary/20 text-primary text-xs font-mono">{chartCount}/4</span>
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-white/10">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Zap className="w-4 h-4 text-yellow-400" />
                      {language === 'fr' ? 'Génération auto · 4 timeframes' : 'Auto generation · 4 timeframes'}
                    </h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      {language === 'fr'
                        ? "Nous générons pour vous les graphiques 15m · 1H · 1D · 1W avec les indicateurs standards, exactement dans le format sur lequel l'IA a été entraînée. Pas de screenshot à faire, pas d'indicateurs custom à gérer — résultats consistants garantis."
                        : "We generate the 15m · 1H · 1D · 1W charts for you with the exact indicators the AI was trained on. No screenshot needed, no custom indicators — consistent reads guaranteed."}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="px-2 py-1 rounded bg-orange-500/20 text-orange-400 font-mono">15m</span>
                      <span className="px-2 py-1 rounded bg-blue-500/20 text-blue-400 font-mono">1H</span>
                      <span className="px-2 py-1 rounded bg-green-500/20 text-green-400 font-mono">1D</span>
                      <span className="px-2 py-1 rounded bg-purple-500/20 text-purple-400 font-mono">1W</span>
                      <span className="text-muted-foreground mx-2">|</span>
                      <span className="px-2 py-1 rounded bg-secondary text-foreground">RSI</span>
                      <span className="px-2 py-1 rounded bg-secondary text-foreground">MACD</span>
                      <span className="px-2 py-1 rounded bg-secondary text-foreground">MA50/200</span>
                      <span className="px-2 py-1 rounded bg-secondary text-foreground">Volume</span>
                    </div>
                  </div>

                  <Button
                    onClick={analyzeAutoCharts}
                    disabled={generatingCharts || !formData.symbol}
                    className="w-full py-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                    data-testid="generate-charts-btn"
                  >
                    {generatingCharts ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        {language === 'fr' ? 'Génération en cours...' : 'Generating...'}
                      </>
                    ) : (
                      <>
                        <Zap className="w-5 h-5 mr-2" />
                        {language === 'fr' ? 'Générer les 4 graphiques' : 'Generate 4 charts'}
                      </>
                    )}
                  </Button>

                  {/* Show generated charts preview - 4 timeframes */}
                  {hasAnyChart && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium flex items-center gap-2">
                          {language === 'fr' ? 'Graphiques générés' : 'Generated Charts'} ({chartCount}/4)
                          <span className="text-xs text-muted-foreground">
                            ({language === 'fr' ? 'Cliquez pour agrandir' : 'Click to zoom'})
                          </span>
                        </span>
                        {chartCount === 4 && (
                          <span className="text-xs text-primary flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            {language === 'fr' ? 'Complet' : 'Complete'}
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                        {charts.m15 && (
                          <div
                            className="relative rounded-lg overflow-hidden border border-orange-500/30 bg-black/20 cursor-pointer hover:border-orange-500 hover:shadow-lg hover:shadow-orange-500/20 transition-all group"
                            onClick={() => setSelectedChart({ src: charts.m15, label: '15 Minutes', timeframe: '15m' })}
                          >
                            <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-orange-500/80 text-xs font-mono text-white z-10">15m</div>
                            <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                              <ZoomIn className="w-4 h-4 text-white drop-shadow-lg" />
                            </div>
                            <img src={charts.m15} alt="15m Chart" className="w-full h-24 object-cover group-hover:scale-105 transition-transform" />
                          </div>
                        )}
                        {charts.hourly && (
                          <div
                            className="relative rounded-lg overflow-hidden border border-blue-500/30 bg-black/20 cursor-pointer hover:border-blue-500 hover:shadow-lg hover:shadow-blue-500/20 transition-all group"
                            onClick={() => setSelectedChart({ src: charts.hourly, label: '1 Heure', timeframe: '1H' })}
                          >
                            <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-blue-500/80 text-xs font-mono text-white z-10">1H</div>
                            <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                              <ZoomIn className="w-4 h-4 text-white drop-shadow-lg" />
                            </div>
                            <img src={charts.hourly} alt="1H Chart" className="w-full h-24 object-cover group-hover:scale-105 transition-transform" />
                          </div>
                        )}
                        {charts.daily && (
                          <div
                            className="relative rounded-lg overflow-hidden border border-green-500/30 bg-black/20 cursor-pointer hover:border-green-500 hover:shadow-lg hover:shadow-green-500/20 transition-all group"
                            onClick={() => setSelectedChart({ src: charts.daily, label: '1 Jour (Swing)', timeframe: '1D' })}
                          >
                            <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-green-500/80 text-xs font-mono text-white z-10">1D</div>
                            <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                              <ZoomIn className="w-4 h-4 text-white drop-shadow-lg" />
                            </div>
                            <img src={charts.daily} alt="1D Chart" className="w-full h-24 object-cover group-hover:scale-105 transition-transform" />
                          </div>
                        )}
                        {charts.weekly && (
                          <div
                            className="relative rounded-lg overflow-hidden border border-purple-500/30 bg-black/20 cursor-pointer hover:border-purple-500 hover:shadow-lg hover:shadow-purple-500/20 transition-all group"
                            onClick={() => setSelectedChart({ src: charts.weekly, label: '1 Semaine (Swing)', timeframe: '1W' })}
                          >
                            <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-purple-500/80 text-xs font-mono text-white z-10">1W</div>
                            <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                              <ZoomIn className="w-4 h-4 text-white drop-shadow-lg" />
                            </div>
                            <img src={charts.weekly} alt="1W Chart" className="w-full h-24 object-cover group-hover:scale-105 transition-transform" />
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                        <BarChart3 className="w-3 h-3" />
                        {language === 'fr'
                          ? 'Indicateurs inclus : MA50, MA200, RSI, MACD'
                          : 'Indicators included: MA50, MA200, RSI, MACD'}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Analysis Results */}
            {analysis && (
              <Card className="bg-card border-white/10 animate-fade-in">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 flex-wrap">
                    <Target className="w-5 h-5 text-primary" />
                    {language === 'fr' ? 'Analyse ICT' : 'ICT Analysis'}
                    <span className="px-3 py-1 rounded-lg bg-primary/20 text-primary text-sm font-bold">{getSymbolName(formData.symbol)}</span>
                    <span className="text-xs text-muted-foreground font-mono">({formData.symbol})</span>
                    <span className="ml-2 px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-400 text-xs font-mono flex items-center gap-1"><Zap className="w-3 h-3" />{language === 'fr' ? 'Levier par profil' : 'Leverage by profile'}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* SWING vs INTRADAY Tabs */}
                  <Tabs defaultValue="swing" className="w-full">
                    <TabsList className="grid w-full grid-cols-2 bg-secondary/50">
                      <TabsTrigger value="swing" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400"><Calendar className="w-4 h-4 mr-2" />SWING</TabsTrigger>
                      <TabsTrigger value="intraday" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400"><Clock className="w-4 h-4 mr-2" />INTRADAY</TabsTrigger>
                    </TabsList>
                    <TabsContent value="swing" className="mt-4"><TradeCard type="swing" analysis={analysis} language={language} symbol={formData.symbol} onSaveTrade={saveTrade} /></TabsContent>
                    <TabsContent value="intraday" className="mt-4"><TradeCard type="intraday" analysis={analysis} language={language} symbol={formData.symbol} onSaveTrade={saveTrade} /></TabsContent>
                  </Tabs>

                  {/* ICT Analysis Section */}
                  <div className="p-4 rounded-xl bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-white/10">
                    <div className="text-sm font-medium mb-3 flex items-center gap-2"><BookOpen className="w-4 h-4 text-purple-400" />{language === 'fr' ? 'Concepts ICT Identifiés' : 'ICT Concepts Identified'}</div>
                    <p className="text-sm leading-relaxed mb-4">{analysis.ict_analysis}</p>
                    
                    <div className="text-sm font-medium mb-2 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-blue-400" />{language === 'fr' ? 'Structure de Marché' : 'Market Structure'}</div>
                    <p className="text-sm leading-relaxed mb-4">{analysis.market_structure}</p>
                    
                    {analysis.mtf_confluence && (
                      <>
                        <div className="text-sm font-medium mb-2 flex items-center gap-2"><Layers className="w-4 h-4 text-green-400" />{language === 'fr' ? 'Confluence MTF' : 'MTF Confluence'}</div>
                        <p className="text-sm leading-relaxed mb-4">{analysis.mtf_confluence}</p>
                      </>
                    )}
                  </div>

                  {/* Technical Indicators Analysis */}
                  <div className="p-4 rounded-xl bg-gradient-to-r from-cyan-500/10 to-teal-500/10 border border-white/10">
                    <div className="text-sm font-medium mb-3 flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-cyan-400" />
                      {language === 'fr' ? 'Indicateurs Techniques' : 'Technical Indicators'}
                    </div>
                    
                    <div className="grid gap-3">
                      {/* Moving Averages */}
                      {analysis.ma_analysis && (
                        <div className="p-3 rounded-lg bg-secondary/30">
                          <div className="text-xs font-medium text-yellow-400 mb-1 flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" /> MA50 / MA200
                          </div>
                          <p className="text-sm">{analysis.ma_analysis}</p>
                        </div>
                      )}
                      
                      {/* MACD */}
                      <div className="p-3 rounded-lg bg-secondary/30">
                        <div className="text-xs font-medium text-cyan-400 mb-1 flex items-center gap-1">
                          <TrendingUp className="w-3 h-3" /> MACD
                        </div>
                        <p className="text-sm">{analysis.macd_analysis}</p>
                      </div>
                      
                      {/* RSI */}
                      <div className="p-3 rounded-lg bg-secondary/30">
                        <div className="text-xs font-medium text-orange-400 mb-1 flex items-center gap-1">
                          <Target className="w-3 h-3" /> RSI
                        </div>
                        <p className="text-sm">{analysis.rsi_analysis}</p>
                      </div>
                      
                      {/* Volume */}
                      <div className="p-3 rounded-lg bg-secondary/30">
                        <div className="text-xs font-medium text-green-400 mb-1 flex items-center gap-1">
                          <BarChart3 className="w-3 h-3" /> Volume
                        </div>
                        <p className="text-sm">{analysis.volume_analysis}</p>
                      </div>
                    </div>
                  </div>

                  {/* Key Levels */}
                  {analysis.key_levels?.length > 0 && (
                    <div className="p-4 rounded-xl bg-secondary/30">
                      <div className="text-sm font-medium mb-2">{language === 'fr' ? 'Niveaux Clés (OB, FVG, Liquidité)' : 'Key Levels (OB, FVG, Liquidity)'}</div>
                      <div className="flex flex-wrap gap-2">
                        {analysis.key_levels.map((level, i) => <span key={i} className="px-3 py-1 rounded-full bg-muted text-sm font-mono">{level}</span>)}
                      </div>
                    </div>
                  )}
                  
                  {/* Kill Zones */}
                  {analysis.kill_zones && (
                    <div className="p-4 rounded-xl bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-white/10">
                      <div className="text-sm font-medium mb-3 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-orange-400" />
                        {language === 'fr' ? 'Kill Zones ICT' : 'ICT Kill Zones'}
                        <span className="text-xs text-muted-foreground ml-2">({analysis.user_timezone})</span>
                      </div>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        {Object.entries(analysis.kill_zones).map(([key, zone]) => (
                          <div 
                            key={key} 
                            className={`p-3 rounded-lg ${
                              zone.quality === 'BEST' ? 'bg-green-500/20 border border-green-500/30' : 
                              zone.quality === 'HIGH' ? 'bg-blue-500/20 border border-blue-500/30' : 
                              zone.quality === 'MEDIUM' ? 'bg-yellow-500/20 border border-yellow-500/30' : 
                              'bg-red-500/10 border border-red-500/20'
                            }`}
                          >
                            <div className="text-xs font-medium mb-1">{zone.name}</div>
                            <div className="font-mono text-lg font-bold">{zone.time}</div>
                            <div className="text-xs text-muted-foreground mt-1">{zone.description}</div>
                            {zone.quality === 'BEST' && (
                              <span className="inline-block mt-2 px-2 py-0.5 rounded text-xs bg-green-500/30 text-green-400">
                                {language === 'fr' ? 'Recommandée' : 'Recommended'}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4">
            <Card className="bg-card border-white/10 sticky top-24">
              <CardHeader><CardTitle>{language === 'fr' ? 'Paramètres' : 'Settings'}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('dashboard.symbol')}</Label>
                  <div className="relative">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input 
                          value={formData.symbol} 
                          onChange={(e) => setFormData({ ...formData, symbol: e.target.value })} 
                          onFocus={() => setShowSymbolDropdown(true)}
                          placeholder={language === 'fr' ? 'Ex: BTC/USDT, NASDAQ:AAPL' : 'Ex: BTC/USDT, NASDAQ:AAPL'}
                          className="bg-secondary/50 border-white/10 font-mono pr-10" 
                          data-testid="symbol-input"
                        />
                        <button 
                          type="button"
                          onClick={() => setShowSymbolDropdown(!showSymbolDropdown)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-white/10 rounded"
                        >
                          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${showSymbolDropdown ? 'rotate-180' : ''}`} />
                        </button>
                      </div>
                    </div>
                    
                    {/* Dropdown for watchlist symbols */}
                    {showSymbolDropdown && watchlist.length > 0 && (
                      <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-card border border-white/10 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                        <div className="p-2 border-b border-white/10 flex items-center gap-2 text-xs text-muted-foreground">
                          <Star className="w-3 h-3 text-yellow-400" />
                          {language === 'fr' ? 'Mes Favoris (triés par catégorie)' : 'My Favorites (sorted by category)'}
                        </div>
                        {/* Sort watchlist by category */}
                        {(() => {
                          const categoryOrder = ['crypto', 'stocks', 'etf', 'forex', 'indices', 'commodities', 'commodity', 'custom'];
                          const sortedWatchlist = [...watchlist].sort((a, b) => {
                            const catA = categoryOrder.indexOf(a.category?.toLowerCase() || 'custom');
                            const catB = categoryOrder.indexOf(b.category?.toLowerCase() || 'custom');
                            if (catA !== catB) return catA - catB;
                            // Same category: sort alphabetically by name
                            return (a.name || a.symbol).localeCompare(b.name || b.symbol);
                          });
                          
                          let currentCategory = null;
                          return sortedWatchlist.map((item, index) => {
                            const showCategoryHeader = item.category !== currentCategory;
                            currentCategory = item.category;
                            
                            return (
                              <div key={item.symbol || index}>
                                {showCategoryHeader && (
                                  <div className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border-t border-white/5 ${
                                    item.category === 'crypto' ? 'bg-orange-500/10 text-orange-400' :
                                    item.category === 'stocks' ? 'bg-green-500/10 text-green-400' :
                                    item.category === 'etf' ? 'bg-purple-500/10 text-purple-400' :
                                    item.category === 'forex' ? 'bg-blue-500/10 text-blue-400' :
                                    item.category === 'indices' ? 'bg-cyan-500/10 text-cyan-400' :
                                    item.category === 'commodities' || item.category === 'commodity' ? 'bg-yellow-500/10 text-yellow-400' :
                                    'bg-gray-500/10 text-gray-400'
                                  }`}>
                                    {item.category === 'crypto' ? '🪙 Crypto' :
                                     item.category === 'stocks' ? '📈 Actions' :
                                     item.category === 'etf' ? '📊 ETF' :
                                     item.category === 'forex' ? '💱 Forex' :
                                     item.category === 'indices' ? '📉 Indices' :
                                     item.category === 'commodities' || item.category === 'commodity' ? '🛢️ Matières Premières' :
                                     '📌 Autre'}
                                  </div>
                                )}
                                <button
                                  type="button"
                                  onClick={() => {
                                    let displaySymbol = item.symbol;
                                    if (item.symbol.includes(':')) {
                                      const ticker = item.symbol.split(':')[1];
                                      if (ticker.endsWith('USDT')) {
                                        displaySymbol = ticker.replace('USDT', '/USDT');
                                      } else if (ticker.endsWith('USD') && !ticker.endsWith('USDT')) {
                                        displaySymbol = ticker.replace('USD', '/USD');
                                      } else {
                                        displaySymbol = ticker;
                                      }
                                    }
                                    setFormData({ ...formData, symbol: displaySymbol });
                                    setShowSymbolDropdown(false);
                                  }}
                                  className="w-full px-3 py-2 text-left hover:bg-white/5 flex items-center justify-between gap-2 transition-colors"
                                  data-testid={`watchlist-option-${index}`}
                                >
                                  <div className="flex flex-col">
                                    <span className="font-medium text-sm">{item.name}</span>
                                    <span className="text-xs text-muted-foreground font-mono">{item.symbol}</span>
                                  </div>
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    item.category === 'crypto' ? 'bg-orange-500/20 text-orange-400' :
                                    item.category === 'stocks' ? 'bg-green-500/20 text-green-400' :
                                    item.category === 'etf' ? 'bg-purple-500/20 text-purple-400' :
                                    item.category === 'forex' ? 'bg-blue-500/20 text-blue-400' :
                                    'bg-gray-500/20 text-gray-400'
                                  }`}>
                                    {item.category}
                                  </span>
                                </button>
                              </div>
                            );
                          });
                        })()}
                        <div className="p-2 border-t border-white/10 text-xs text-muted-foreground text-center">
                          {language === 'fr' ? 'Ou saisissez un symbole manuellement' : 'Or type a symbol manually'}
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Click outside to close */}
                  {showSymbolDropdown && (
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setShowSymbolDropdown(false)}
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Zap className="w-4 h-4 text-yellow-400" />{language === 'fr' ? 'Levier (par profil)' : 'Leverage (by profile)'}</Label>
                  <div className="p-3 rounded-lg bg-secondary/30 text-xs space-y-1">
                    <div className="flex items-center gap-2">
                      <Shield className="w-3 h-3 text-green-400" />
                      <span className="text-green-400">Prudent:</span>
                      <span className="font-mono">10x</span>
                      <span className="text-muted-foreground text-[10px]">• TP proche</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Scale className="w-3 h-3 text-yellow-400" />
                      <span className="text-yellow-400">Équilibré:</span>
                      <span className="font-mono">20x</span>
                      <span className="text-muted-foreground text-[10px]">• TP moyen</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Flame className="w-3 h-3 text-red-400" />
                      <span className="text-red-400">Dynamique:</span>
                      <span className="font-mono">30x</span>
                      <span className="text-muted-foreground text-[10px]">• TP éloigné</span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {language === 'fr' ? 'SL identique (ICT) • TP plus loin = plus de gains mais plus exposé au retournement' : 'Same SL (ICT) • Farther TP = more gains but more exposure to reversal'}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>{t('dashboard.capital')}</Label>
                  <Input type="number" value={formData.capital} onChange={(e) => setFormData({ ...formData, capital: parseFloat(e.target.value) || 0 })} className="bg-secondary/50 border-white/10 font-mono" />
                </div>
                <div className="space-y-2">
                  <Label>{t('dashboard.risk')}</Label>
                  <Input type="number" value={formData.risk} step="0.5" min="0.5" max="5" onChange={(e) => setFormData({ ...formData, risk: parseFloat(e.target.value) || 1 })} className="bg-secondary/50 border-white/10 font-mono" />
                </div>
                <div className="p-3 rounded-lg bg-secondary/30 text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-muted-foreground">{language === 'fr' ? 'Risque Max' : 'Max Risk'}:</span><span className="font-mono text-destructive">${(formData.capital * formData.risk / 100).toFixed(2)}</span></div>
                </div>
                
                {/* Free Analysis Credits Display */}
                {isAuthenticated && user?.free_analyses && (user.free_analyses.swing > 0 || user.free_analyses.intraday > 0) && (
                  <div className="p-3 rounded-lg bg-gradient-to-r from-primary/10 to-emerald-500/10 border border-primary/30">
                    <div className="flex items-center gap-2 text-sm">
                      <Sparkles className="w-4 h-4 text-primary" />
                      <span className="font-medium text-primary">
                        {language === 'fr' ? 'Analyses gratuites :' : 'Free analyses:'}
                      </span>
                      <span className="font-mono">
                        {user.free_analyses.swing > 0 && `${user.free_analyses.swing} Swing`}
                        {user.free_analyses.swing > 0 && user.free_analyses.intraday > 0 && ' + '}
                        {user.free_analyses.intraday > 0 && `${user.free_analyses.intraday} Intraday`}
                      </span>
                    </div>
                  </div>
                )}
                
                <Button 
                  onClick={handleAnalyzeClick}
                  disabled={!hasAnyChart || analyzing} 
                  className="w-full py-6 font-mono uppercase bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="analyze-btn"
                >
                  {analyzing ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" />{language === 'fr' ? 'Analyse ICT...' : 'ICT Analysis...'}</>
                  ) : (
                    <><Target className="w-5 h-5 mr-2" />{language === 'fr' ? `Analyser (${chartCount})` : `Analyze (${chartCount})`}</>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Chart Zoom Modal */}
      <Dialog open={!!selectedChart} onOpenChange={() => setSelectedChart(null)}>
        <DialogContent className="max-w-6xl w-[95vw] max-h-[95vh] p-0 bg-[#1a1a2e] border-white/10">
          <DialogHeader className="p-4 pb-2 border-b border-white/10">
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Maximize2 className="w-5 h-5 text-primary" />
                <span>{formData.symbol} - {selectedChart?.label}</span>
                <span className="px-2 py-0.5 rounded bg-primary/20 text-primary text-xs font-mono">
                  {selectedChart?.timeframe}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <BarChart3 className="w-4 h-4" />
                MA50 • MA200 • RSI • MACD
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="p-4 overflow-auto max-h-[calc(95vh-80px)]">
            {selectedChart && (
              <img 
                src={selectedChart.src} 
                alt={`${formData.symbol} ${selectedChart.timeframe} Chart`}
                className="w-full h-auto rounded-lg"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
